import React from 'react';

/**
 * FormSteps Component
 *
 * A reusable component for creating consistent form steps in the onboarding process
 *
 * @param {String} header - The title of the form step
 * @param {String} description - Optional description text
 * @param {Function} nextButton - Function to handle next button click
 * @param {Function} skipButton - Optional function to handle skip button click
 * @param {Function} prevButton - Optional function to handle back button click
 * @param {ReactNode} children - The form content to be rendered
 * @param {String} nextButtonText - Optional text for next button (defaults to "Next")
 * @param {Boolean} isSubmitting - Optional flag for loading state on submit
 */

interface FormStepsProps {
  header: string;
  description?: string;
  nextButton: () => void;
  skipButton?: () => void;
  children: React.ReactNode;
  nextButtonText?: string;
  isSubmitting?: boolean;
}

const FormSteps: React.FC<FormStepsProps> = ({
  header,
  description,
  nextButton,
  skipButton,
  children,
  nextButtonText = 'Next',
  isSubmitting = false
}) => {
  return (
    <>
      <header className="content">
        <h1 className="is-size-5">{header}</h1>
        {description && (
          <span className="is-small text-muted">{description}</span>
        )}
      </header>

      <div className="form-content">{children}</div>

      <div className="button-group">
        {skipButton && (
          <button
            type="button"
            className="button-skip"
            onClick={skipButton}
          >
            Skip
          </button>
        )}
        <button
          type="button"
          className={`button-next ${isSubmitting ? 'is-loading' : ''}`}
          onClick={nextButton}
          disabled={isSubmitting}
        >
          {nextButtonText}
        </button>
      </div>
    </>
  );
};

export default FormSteps;
